import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import type { ShowForRule } from '@art-of-hospitality-solutions-llc/ui-kit/components/content-blocks';
import { Plus } from 'lucide-react';

import EditorGroup from '../EditorGroup';
import SchemaFields, {
  type SchemaFieldsContext,
} from '../EditorFields/SchemaFields';
import type { MultiSelectOption } from '../EditorFields/MultiSelectField';
import ShowForEditor from '../ShowForEditor';
import type { BlockDef, BlockNode } from '../types';

interface ComponentEditorProps extends SchemaFieldsContext {
  /** The selected block; undefined renders the empty state. */
  node?: BlockNode;
  /** The selected block's registry definition. */
  blockDef?: BlockDef;
  onPropChange: (propName: string, value: unknown) => void;
  /**
   * Sets/clears the node's conditional-rendering rule. Present on every
   * block — gating is node-level metadata, not a schema prop.
   */
  onShowForChange?: (rule: ShowForRule | undefined) => void;
  tagOptions?: MultiSelectOption[];
  permissionOptions?: MultiSelectOption[];
  roleOptions?: MultiSelectOption[];
  /**
   * Opens the add-component dialog targeting this block. Provided only when
   * the block can take more children (container, placeable child types,
   * capacity/locks permitting) — absence hides the button.
   */
  onAddComponent?: () => void;
}

/**
 * Settings panel for the selected block. The controls are generated from the
 * block's prop schema — accordion groups come from each prop's `group`
 * annotation, in schema declaration order. (The layer name is edited inline
 * in the layers panel, not here — it's outline metadata, not a setting.)
 */
export default function ComponentEditor({
  node,
  blockDef,
  onPropChange,
  onShowForChange,
  tagOptions = [],
  permissionOptions = [],
  roleOptions = [],
  onAddComponent,
  ...context
}: ComponentEditorProps) {
  if (!node || !blockDef) {
    return (
      <div className="flex h-full items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-400">
          Select a component from the layers panel to edit
        </p>
      </div>
    );
  }

  const groups = Object.values(blockDef.schema.properties).reduce<string[]>(
    (acc, property) => {
      const group = property.group ?? 'Content';
      if (property.editable !== false && !acc.includes(group)) acc.push(group);
      return acc;
    },
    [],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 p-2">
        <h3 className="font-medium">{blockDef.displayName}</h3>
        <div className="text-sm text-zinc-500">Component settings</div>
      </div>
      <div className="min-h-0 overflow-y-auto">
        <div className="p-3">
          {groups.map((group) => (
            <EditorGroup key={group} name={group}>
              <SchemaFields
                properties={blockDef.schema.properties}
                values={node.props}
                group={group}
                onChange={onPropChange}
                {...context}
              />
            </EditorGroup>
          ))}
          {onShowForChange && (
            <EditorGroup name="Show For">
              <ShowForEditor
                value={node.showFor}
                tagOptions={tagOptions}
                permissionOptions={permissionOptions}
                roleOptions={roleOptions}
                onChange={onShowForChange}
              />
            </EditorGroup>
          )}
          {groups.length === 0 && !onShowForChange && !onAddComponent && (
            <p className="py-4 text-center text-sm text-zinc-400">
              This block has no editable settings.
            </p>
          )}
          {onAddComponent && (
            <div className="mt-4">
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={onAddComponent}
              >
                <Plus size={14} />
                Add component
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
